import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Col, Divider, Row } from 'antd';
import { AtsButtonMenu, AtsSkeleton, Button, BUTTON_TYPES, Status, STATUS_TYPES } from '_atoms';
import { Typography, TYPOGRAPHY_WEIGHT } from '_atoms/Typography';
import { PopupCreateInterview, PopupCancelInterview, PopupConfirmInterview } from '_molecules';
import {
  getCurrentInterview,
  getInterviewSequenceNumber,
  groupFeedbacksByInterview,
  INTERVIEW_KINDS,
  type InterviewKind,
} from 'constants/interview';
import { getInterviewFeedbacks, getJobInterviewLoading, getJobInterviews } from 'store/selectors/jobSelectors';
import { Can, ACTIONS, SUBJECTS, useAbility } from 'permission';
import { ReactComponent as ButtonIcon } from 'dist/icons/button_icon.svg';
import { ReactComponent as Date } from 'dist/images/date.svg';
import { ReactComponent as Comment } from 'dist/images/comment.svg';
import { utcTime, dateFormatter } from 'helpers/dateFormatter';
import type { SaveInterviewPayload } from 'actions/jobActions';
import type { Interview as InterviewEntity } from 'store/reducers/jobReducer';
import type { Uuid } from 'store/reducers/types';
import { InterviewFeedback } from '../InterviewFeedback';
import { useInterviewData } from './utils';
import './style.scss';

interface InterviewUserData {
  first_name_latin?: string;
  last_name_latin?: string;
  first_name_local?: string;
  last_name_local?: string;
  full_name_latin?: string;
  full_name_local?: string;
  specialities?: Array<{ level?: string; speciality?: string }>;
}

const INTERVIEW_TITLE_KEYS = {
  [INTERVIEW_KINDS.HR]: 'translation:interview.title_hr',
  [INTERVIEW_KINDS.TECHNICAL]: 'translation:interview.title_technical',
  [INTERVIEW_KINDS.MANAGEMENT]: 'translation:interview.title_management',
} as const;

const EMPTY_INTERVIEW: InterviewEntity = {};

interface InterviewProps {
  accessToGoogleCalendar?: boolean;
  cancelInterview: (uuid: Uuid | undefined, data: unknown) => void;
  canCreate?: boolean;
  canAddAdditional?: boolean;
  interviewKind: InterviewKind;
  rejected?: boolean;
  stepId: Uuid;
  createInterview: (data: SaveInterviewPayload) => void;
  confirmInterview: (uuid: Uuid | undefined, cb: (value: boolean) => void) => void;
  userData: InterviewUserData;
  onFeedbackChanged?: () => void;
  sourceStepName?: string;
}

export const Interview: React.FC<InterviewProps> = ({
  accessToGoogleCalendar,
  cancelInterview,
  canCreate = true,
  canAddAdditional = false,
  interviewKind,
  rejected,
  stepId,
  createInterview,
  confirmInterview,
  userData,
  onFeedbackChanged,
  sourceStepName,
}) => {
  const ability = useAbility();
  const { t } = useTranslation();
  const interviews = useSelector(getJobInterviews(stepId));
  const stepFeedbacks = useSelector(getInterviewFeedbacks(stepId));
  const feedbackGroups = useMemo(
    () => groupFeedbacksByInterview(stepFeedbacks, interviews),
    [interviews, stepFeedbacks]
  );
  const feedbackGroupsByInterviewId = useMemo(() => {
    const groups = new Map(feedbackGroups.map(group => [group.interviewId, group]));
    return groups;
  }, [feedbackGroups]);
  const interviewsNewestFirst = useMemo(
    () =>
      interviews
        .map((interview, index) => ({
          interview,
          sequence: getInterviewSequenceNumber(interview, index),
        }))
        .reverse(),
    [interviews]
  );
  const title = t(INTERVIEW_TITLE_KEYS[interviewKind]);
  const empty = !interviews.length;
  const loading = useSelector(getJobInterviewLoading(stepId));
  const [showCreationPopup, setShowCreationPopup] = useState(false);
  const [isCreatingAdditional, setIsCreatingAdditional] = useState(false);
  const [activeInterview, setActiveInterview] = useState<InterviewEntity>(EMPTY_INTERVIEW);
  const [openConfirmInterview, setOpenConfirmInterview] = useState(false);
  const [openDeleteInterview, setOpenDeleteInterview] = useState(false);

  const onOpenCreatePopup = (additional: boolean) => {
    setIsCreatingAdditional(additional);
    setActiveInterview(EMPTY_INTERVIEW);
    setShowCreationPopup(true);
  };
  const onOpenEditPopup = (interview: InterviewEntity) => {
    setIsCreatingAdditional(false);
    setActiveInterview(interview);
    setShowCreationPopup(true);
  };
  const onClosePopup = () => {
    setShowCreationPopup(false);
    setIsCreatingAdditional(false);
    setActiveInterview(EMPTY_INTERVIEW);
  };

  const onSubmit = values => {
    setShowCreationPopup(false);
    createInterview({
      ...values,
      locations: values.locations.map(({ value }) => value),
      create_google_event: !!values.create_google_event,
      date: values.date.format('YYYY-MM-DD'),
      type: values.type.value,
      from_time: utcTime(values.from_time.value || '12:00'),
      to_time: utcTime(values.to_time.value || '13:00'),
      interview_type: interviewKind,
    });
    setIsCreatingAdditional(false);
    setActiveInterview(EMPTY_INTERVIEW);
  };

  const onOpenDeletePopup = (interview: InterviewEntity) => {
    setActiveInterview(interview);
    setOpenDeleteInterview(true);
  };
  const onCloseDeletePopup = () => {
    setOpenDeleteInterview(false);
    setActiveInterview(EMPTY_INTERVIEW);
  };

  const onOpenConfirmPopup = (interview: InterviewEntity) => {
    setActiveInterview(interview);
    setOpenConfirmInterview(true);
  };
  const onCloseConfirmPopup = () => {
    setOpenConfirmInterview(false);
    setActiveInterview(EMPTY_INTERVIEW);
  };

  const onCancelInterview = data => {
    cancelInterview(activeInterview.uuid, data);
    onCloseDeletePopup();
  };

  const showEditInterview = ability.can(ACTIONS.UPDATE, SUBJECTS.INTERVIEW);
  const deleteInterview = ability.can(ACTIONS.DELETE, SUBJECTS.INTERVIEW);
  const initialValues = useInterviewData(activeInterview, accessToGoogleCalendar);
  const onConfirmInterview = () =>
    confirmInterview(activeInterview.uuid, () => {
      onCloseConfirmPopup();
    });

  const showEmptyAdd = empty;
  const showAdditionalAdd = !empty && canAddAdditional && !rejected;
  const currentInterviewUuid = getCurrentInterview(interviews)?.uuid;

  const renderAddButton = (additional: boolean) => (
    <Can I={ACTIONS.CREATE} a={SUBJECTS.INTERVIEW} passThrough>
      {access =>
        access ? (
          <Button
            className="job-interview__add-button"
            icon={<ButtonIcon />}
            onClick={() => onOpenCreatePopup(additional)}
            disabled={rejected || (empty && !canCreate)}
          >
            {t('translation:interview.add')}
          </Button>
        ) : (
          empty && <Typography.Text>{t('translation:interview.not_scheduled')}</Typography.Text>
        )
      }
    </Can>
  );

  if (loading) {
    return (
      <div className="job-interview">
        <Typography.Title2 weight={TYPOGRAPHY_WEIGHT.BOLD} className="job-interview__title">
          {title}
        </Typography.Title2>
        <AtsSkeleton height={36} />
        <Divider />
      </div>
    );
  }

  return (
    <div className="job-interview">
      <Typography.Title2 weight={TYPOGRAPHY_WEIGHT.BOLD} className="job-interview__title">
        {title}
        {interviewKind === INTERVIEW_KINDS.HR && empty && (
          <span className="job-interview__optional">({t('translation:interview.optional')})</span>
        )}
      </Typography.Title2>

      {(showEmptyAdd || showAdditionalAdd) && renderAddButton(showAdditionalAdd)}

      {!empty && (
        <div className="job-interview__list">
          {interviewsNewestFirst.map(({ interview, sequence }) => {
            const isCurrent = interview.is_current === true || interview.uuid === currentInterviewUuid;
            const isCompleted = !isCurrent && !interview.is_need_confirm;
            const isTimePassed =
              interview.date && moment().isAfter(moment(`${interview.date}T${interview.from_time}Z`));
            const actionsDisabled = !interview.is_need_confirm && isTimePassed;
            const roomNames = interview.locations
              ?.map(el => el.name)
              .filter(Boolean)
              .join(', ');

            return (
              <div key={interview.uuid || sequence} className="job-interview__card">
                <div className="job-interview__card-header">
                  <div className="job-interview__card-title">
                    <Typography.Title3 weight={TYPOGRAPHY_WEIGHT.BOLD}>
                      {t('translation:interview.round_title', {
                        number: sequence,
                      } as Record<string, unknown>)}
                    </Typography.Title3>
                    {isCurrent && <Status type={STATUS_TYPES.BLUE}>{t('translation:interview.status_current')}</Status>}
                    {isCompleted && (
                      <Status type={STATUS_TYPES.GRAY}>{t('translation:interview.status_completed')}</Status>
                    )}
                  </div>
                  {isCurrent && (
                    <div className="job-interview__card-actions">
                      {interview.is_need_confirm && (
                        <Button
                          className="job-interview__confirm"
                          type={BUTTON_TYPES.GHOST}
                          onClick={() => onOpenConfirmPopup(interview)}
                        >
                          {t('translation:interview.confirm')}
                        </Button>
                      )}
                      <AtsButtonMenu disabled={actionsDisabled} className="job-interview__menu">
                        {showEditInterview && (
                          <li role="none" onClick={() => onOpenEditPopup(interview)}>
                            {t('translation:interview.edit')}
                          </li>
                        )}
                        {deleteInterview && (
                          <li role="none" onClick={() => onOpenDeletePopup(interview)}>
                            {t('translation:interview.cancel')}
                          </li>
                        )}
                      </AtsButtonMenu>
                    </div>
                  )}
                </div>
                <Row gutter={[32, 12]}>
                  <Col span={24} lg={12} md={12} sm={24}>
                    <div className="interview-item">
                      <Date className="interview-item__icon" />
                      <div className="interview-item__info">
                        <div className="title">
                          {t(
                            !interview.is_need_confirm && isTimePassed
                              ? 'translation:interview.datetime_passed'
                              : 'translation:interview.datetime'
                          )}
                        </div>
                        <div className="info">
                          {dateFormatter(`${interview.date} ${interview.from_time}`, 'ddd MMM DD, YYYY H:mm')}
                          {' – '}
                          {dateFormatter(`${interview.date} ${interview.to_time}`, 'H:mm')}
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col span={24} lg={12} md={12} sm={24}>
                    <div className="interview-item">
                      <Comment className="interview-item__icon" />
                      <div className="interview-item__info">
                        <div className="title">{t('translation:interview.conference_room')}</div>
                        {roomNames && <div className="info">{roomNames}</div>}
                        {interview.link ? (
                          <a href={interview.link} target="_blank" rel="noopener noreferrer" className="info-link">
                            {interview.link}
                          </a>
                        ) : (
                          !roomNames && <div className="info">{t('translation:interview.no_link')}</div>
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>
                {interview.uuid && sourceStepName && !interview.is_need_confirm && (
                  <InterviewFeedback
                    embedded
                    group={
                      feedbackGroupsByInterviewId.get(interview.uuid) || {
                        interviewId: interview.uuid,
                        sequence,
                        isCurrent,
                        feedbacks: [],
                        feedbacksPosted: interview.feedbacks_posted,
                        feedbacksTotal: interview.feedbacks_total,
                      }
                    }
                    groupsCount={feedbackGroups.length || interviews.length}
                    interviewId={interview.uuid}
                    interviewKind={interviewKind}
                    onFeedbackChanged={onFeedbackChanged}
                    readOnly={!isCurrent}
                    sourceStepName={sourceStepName}
                    stepId={stepId}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      <Divider />
      <PopupCreateInterview
        open={showCreationPopup}
        onClose={onClosePopup}
        onOk={onSubmit}
        initialValues={initialValues}
        empty={!activeInterview.uuid}
        isAdditional={isCreatingAdditional || (!activeInterview.uuid && interviews.length > 0)}
        userData={userData}
        accessToCalendar={accessToGoogleCalendar}
      />
      <PopupConfirmInterview
        open={openConfirmInterview}
        startDate={activeInterview.date ? `${activeInterview.date}T${activeInterview.from_time}Z` : null}
        endDate={activeInterview.date ? `${activeInterview.date}T${activeInterview.to_time}Z` : null}
        userData={userData}
        onClose={onCloseConfirmPopup}
        onConfirm={onConfirmInterview}
      />
      <PopupCancelInterview
        open={openDeleteInterview}
        startDate={activeInterview.date ? `${activeInterview.date} ${activeInterview.from_time}` : null}
        endDate={activeInterview.date ? `${activeInterview.date} ${activeInterview.to_time}` : null}
        userData={userData}
        onClose={onCloseDeletePopup}
        onConfirm={onCancelInterview}
      />
    </div>
  );
};
